import {
  ArrowRight,
  CalendarClock,
  CheckCircle2,
  KeyRound,
  LayoutList,
  Link2,
  LoaderCircle,
  LockKeyhole,
  Plus,
  Search,
  Server,
  ShieldCheck,
} from "lucide-react";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate } from "../router";

import { api, ApiError, jsonBody } from "../api";
import { useAuth } from "../auth";
import { LicenseRegistry } from "../components/license-registry";
import {
  Button,
  EmptyState,
  InlineNotice,
  LoadingState,
} from "../components/ui";
import type {
  BuyerOverview,
  LicenseSummary,
  Paged,
  Product,
} from "../types";

export function OverviewPage() {
  const { account } = useAuth();
  const [overview, setOverview] = useState<BuyerOverview | null>(null);
  const [licenses, setLicenses] = useState<LicenseSummary[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api<BuyerOverview>("/v1/web/overview"),
      api<Paged<LicenseSummary>>("/v1/web/licenses?pageSize=5"),
    ])
      .then(([summary, page]) => {
        setOverview(summary);
        setLicenses(page.items);
      })
      .catch((reason) => setError(reason instanceof Error ? reason.message : "Dashboard data could not be loaded."));
  }, []);

  const displayName = account?.displayName?.split(" ")[0] || "there";
  return (
    <div className="page page--overview">
      <header className="page-heading page-heading--split">
        <div>
          <span className="page-context">Buyer workspace</span>
          <h1>Welcome back, {displayName}.</h1>
          <p>Monitor your product access and keep server slots under control.</p>
        </div>
        <Link className="button button--primary" to="/add-license"><Plus size={17} /> Add license</Link>
      </header>

      {error && <InlineNotice kind="danger">{error}</InlineNotice>}
      {!overview && !error ? <LoadingState label="Loading license workspace" /> : overview && (
        <section className="metric-strip" aria-label="License summary">
          <Metric icon={<ShieldCheck size={21} />} label="Active licenses" value={overview.activeLicenses} note={`${overview.totalLicenses} total`} />
          <Metric icon={<CalendarClock size={21} />} label="Expiring soon" value={overview.expiringSoon} note="next 30 days" tone={overview.expiringSoon ? "warning" : "default"} />
          <Metric icon={<Server size={21} />} label="IP slots" value={`${overview.ipSlots.used}/${overview.ipSlots.total}`} note={`${Math.max(0, overview.ipSlots.total - overview.ipSlots.used)} available`} />
          <Metric icon={<Link2 size={21} />} label="Access requests" value={overview.pendingTransfers} note="awaiting action" tone={overview.pendingTransfers ? "warning" : "default"} />
        </section>
      )}

      <div className="overview-grid">
        <section className="section-panel license-panel">
          <div className="section-heading">
            <div>
              <h2>My licenses</h2>
              <p>Live status and capacity for your products.</p>
            </div>
            <Link className="text-link" to="/licenses">View all <ArrowRight size={15} /></Link>
          </div>
          <LicenseRegistry compact licenses={licenses} />
        </section>

        <aside className="account-rail">
          <section className="rail-section rail-section--brand">
            <div className="rail-section__icon"><LockKeyhole size={19} /></div>
            <h2>Ownership protected</h2>
            <p>
              {account?.discordUserId
                ? "Discord ownership is connected. Your eligible licenses are discovered automatically."
                : "This account manages only keys you attach. Discord owners can approve or deny every transfer."}
            </p>
            <span className="identity-state"><CheckCircle2 size={15} /> {account?.discordUserId ? "Discord connected" : "Email account"}</span>
          </section>
          <section className="rail-section">
            <h2>How access works</h2>
            <ol className="process-list">
              <li><span>1</span><p><strong>Add the key</strong><small>Only the full secret key can attach a license.</small></p></li>
              <li><span>2</span><p><strong>Owner is notified</strong><small>The canonical Discord buyer receives a private DM.</small></p></li>
              <li><span>3</span><p><strong>Transfers require approval</strong><small>Old web access is removed atomically.</small></p></li>
            </ol>
          </section>
        </aside>
      </div>
    </div>
  );
}

function Metric({
  icon,
  label,
  note,
  tone = "default",
  value,
}: {
  icon: ReactNode;
  label: string;
  note: string;
  tone?: "default" | "warning";
  value: number | string;
}) {
  return (
    <div className={`metric ${tone === "warning" ? "metric--warning" : ""}`}>
      <span className="metric__icon">{icon}</span>
      <span><small>{label}</small><strong>{value}</strong><em>{note}</em></span>
    </div>
  );
}

export function LicensesPage() {
  const [page, setPage] = useState<Paged<LicenseSummary> | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [search, setSearch] = useState("");
  const [product, setProduct] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  function load() {
    setLoading(true);
    const query = new URLSearchParams({ pageSize: "50" });
    if (search.trim()) query.set("search", search.trim());
    if (product) query.set("product", product);
    if (status) query.set("status", status);
    Promise.all([
      api<Paged<LicenseSummary>>(`/v1/web/licenses?${query}`),
      api<Product[]>("/v1/web/products"),
    ])
      .then(([licensePage, productRows]) => {
        setPage(licensePage);
        setProducts(productRows);
        setError(null);
      })
      .catch((reason) => setError(reason instanceof Error ? reason.message : "Licenses could not be loaded."))
      .finally(() => setLoading(false));
  }

  useEffect(load, [product, status]);

  return (
    <div className="page">
      <header className="page-heading page-heading--split">
        <div>
          <span className="page-context">License registry</span>
          <h1>My licenses</h1>
          <p>Search products, inspect limits, and open a license record.</p>
        </div>
        <Link className="button button--primary" to="/add-license"><Plus size={17} /> Add license</Link>
      </header>
      <section className="section-panel">
        <form
          className="filter-bar"
          onSubmit={(event) => {
            event.preventDefault();
            load();
          }}
        >
          <label className="search-field"><Search size={17} /><input onChange={(event) => setSearch(event.target.value)} placeholder="Search product or key prefix" value={search} /></label>
          <select aria-label="Filter by product" onChange={(event) => setProduct(event.target.value)} value={product}>
            <option value="">All products</option>
            {products.map((row) => <option key={row.id} value={row.slug}>{row.name}</option>)}
          </select>
          <select aria-label="Filter by status" onChange={(event) => setStatus(event.target.value)} value={status}>
            <option value="">All statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="REVOKED">Revoked</option>
          </select>
          <Button type="submit" variant="secondary">Search</Button>
        </form>
        {error && <InlineNotice kind="danger">{error}</InlineNotice>}
        {loading ? <LoadingState label="Loading licenses" /> : (
          <>
            <div className="result-count"><strong>{page?.total || 0}</strong> license{page?.total === 1 ? "" : "s"} found</div>
            <LicenseRegistry licenses={page?.items || []} />
          </>
        )}
      </section>
    </div>
  );
}

export function AddLicensePage() {
  const navigate = useNavigate();
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    licenseId: string;
    mode: string;
    product: { name: string; slug: string };
    expiresAt?: string;
  } | null>(null);

  async function attach(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const attached = await api<{ licenseId: string; mode: string; product: { name: string; slug: string }; expiresAt?: string }>("/v1/web/licenses/attach", {
        body: jsonBody({ licenseKey: key.trim() }),
        method: "POST",
      });
      setResult(attached);
      setKey("");
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "The license could not be attached.");
    } finally {
      setBusy(false);
    }
  }

  if (result) {
    const pending = result.mode.includes("PENDING");
    return (
      <div className="page page--narrow">
        <header className="page-heading"><span className="page-context">Access complete</span><h1>{pending ? "Discord approval requested" : "License added"}</h1></header>
        <section className="attach-success">
          <span className="attach-success__icon"><CheckCircle2 size={28} /></span>
          <h2>{result.product.name}</h2>
          <code>{result.product.slug}</code>
          <p>
            {result.mode === "LINKED_PENDING_CONFIRMATION"
              ? "You can manage this license now. Its Discord owner received a private confirmation request and may deny access if this was not expected."
              : result.mode === "TRANSFER_PENDING"
                ? "The current web account keeps access until the Discord owner approves your transfer."
                : "This license is ready in your workspace."}
          </p>
          <div className="attach-success__actions">
            {!pending && <Button onClick={() => navigate(`/licenses/${result.licenseId}`)}>Open license <ArrowRight size={16} /></Button>}
            <Button onClick={() => setResult(null)} variant="ghost">Add another</Button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="page page--narrow">
      <header className="page-heading">
        <span className="page-context">Secure attachment</span>
        <h1>Add a license</h1>
        <p>Enter the complete key exactly as it was delivered. It is sent over HTTPS and never stored in your browser.</p>
      </header>
      <section className="attach-panel">
        <div className="attach-panel__symbol"><KeyRound size={27} /></div>
        <form onSubmit={(event) => void attach(event)}>
          <label>
            License key
            <input
              autoComplete="off"
              className="license-input"
              maxLength={128}
              minLength={16}
              onChange={(event) => setKey(event.target.value)}
              placeholder="OCL_••••••••••••••••••••••••"
              required
              spellCheck={false}
              value={key}
            />
          </label>
          {error && <InlineNotice kind="danger">{error}</InlineNotice>}
          <Button disabled={busy || key.trim().length < 16} type="submit">
            {busy ? <LoaderCircle className="spin" size={17} /> : <Link2 size={17} />}
            Verify and add license
          </Button>
        </form>
        <div className="attach-rules">
          <h2>Ownership rules</h2>
          <ul>
            <li><ShieldCheck size={17} /><span><strong>Discord stays in control</strong>The buyer tagged when the key was created remains the owner.</span></li>
            <li><LockKeyhole size={17} /><span><strong>One web manager</strong>A license can be attached to only one normal account at a time.</span></li>
            <li><LayoutList size={17} /><span><strong>Safe transfer</strong>A second account waits until the owner approves it by DM.</span></li>
          </ul>
        </div>
      </section>
    </div>
  );
}

export function NotFoundPage() {
  return (
    <div className="page page--narrow">
      <EmptyState
        action={<Link className="button button--primary" to="/dashboard">Return to overview</Link>}
        description="The page may have moved, or your account does not have access to it."
        icon={<Search size={24} />}
        title="Page not found"
      />
    </div>
  );
}
